import { useState } from 'react';
import { useT } from '../../i18n';
import { haptic } from '../../native/platform';
import { useSync } from '../../sync/engine';
import { manualSync } from '../../sync/manual';
import styles from './SyncDot.module.css';

/** The dot shown after a tap has to stay visibly busy, even when the server answers at once. */
const MIN_BUSY_MS = 700;

/** One small dot next to the brand: synced, syncing, offline with changes waiting, or failing. A tap syncs now. */
export function SyncDot() {
  const t = useT();
  const phase = useSync((s) => s.phase);
  const pending = useSync((s) => s.pending);
  const live = useSync((s) => s.live);
  const [busy, setBusy] = useState(false);
  const shown = busy ? 'syncing' : phase;
  const label =
    shown === 'idle' ? t.t(live ? 'sync.live' : 'sync.idle')
    : shown === 'syncing' ? t.t('sync.syncing')
    : shown === 'offline' ? (pending ? t.t('sync.offlinePending', { n: pending }) : t.t('sync.offline'))
    : shown === 'error' ? t.t('sync.error')
    : t.t('sync.signedOut');

  const onClick = async () => {
    if (busy || phase === 'signedOut') return;
    setBusy(true);
    haptic('tick');
    const started = Date.now();
    await manualSync();
    const remaining = MIN_BUSY_MS - (Date.now() - started);
    if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
    setBusy(false);
  };

  return (
    <button
      type="button"
      className={styles.btn}
      onClick={() => void onClick()}
      disabled={phase === 'signedOut'}
      aria-label={`${label}. ${t.t('sync.now')}`}
      title={`${label} · ${t.t('sync.now')}`}
    >
      <span className={styles.dot} data-phase={shown} data-live={live ? '' : undefined} data-pending={pending > 0 ? '' : undefined} />
    </button>
  );
}
